import React, { useState, useRef, useEffect } from 'react';
import WebIcon from './WebIcon';
import { normalizeExternalLink, listMapRouteModes, resolveMapRouteModeMeta } from './itineraryUtils';

type NoteEditorProps = {
  initialText?: string;
  initialMapLink?: string;
  initialMapLinkMode?: string;
  initialMapLinkDuration?: string;
  onCancel?: () => void;
  onSave: (payload: { text: string; mapLink?: string; mapLinkMode?: string; mapLinkDuration?: string }) => Promise<void | { entryId?: string }> | void | { entryId?: string };
  saving?: boolean;
  // 图片相关
  cardId?: string;
  currentUserId?: string | null;
  currentItineraryId?: string;
  gallery?: any[];
  onGalleryUpdate?: (updatedGallery: any[], entryId?: string) => Promise<void>;
  entryId?: string; // 当前 note 的 entry_id（编辑现有 note 时）
};

const ROUTE_MODE_OPTIONS = listMapRouteModes();

const CARD_IMAGE_UPLOAD_ENDPOINT = 
  (typeof window !== 'undefined' && (window as any).__CARD_IMAGE_UPLOAD__) || 
  'https://card-image-upload.cubemeet.app';

const NoteEditor: React.FC<NoteEditorProps> = ({
  initialText = '',
  initialMapLink = '',
  initialMapLinkMode = '',
  initialMapLinkDuration = '',
  onCancel,
  onSave,
  saving = false,
  cardId,
  currentUserId,
  currentItineraryId,
  gallery = [],
  onGalleryUpdate,
  entryId,
}) => {
  const [text, setText] = useState<string>(initialText);
  const [mapLink, setMapLink] = useState<string>(initialMapLink);
  const [mapLinkMode, setMapLinkMode] = useState<string>(initialMapLinkMode);
  const [mapLinkDuration, setMapLinkDuration] = useState<string>(initialMapLinkDuration);
  const [localSaving, setLocalSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingImages, setPendingImages] = useState<Array<{ file: File; preview: string }>>([]);
  const MAX_IMAGE_COUNT = 9;
  const MAX_FILE_SIZE = 8 * 1024 * 1024;
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 过滤：只显示属于当前 note 的图片
  const itineraryImages = gallery.filter(
    (img) => {
      // 如果没有 entryId（新建 note），不显示任何已上传图片
      if (!entryId) return false;
      // 只显示 entry_id 匹配的图片
      if (img.entry_id === entryId) return true;
      // 兼容旧数据：如果图片没有 entry_id，且 itinerary_id 匹配，也显示
      if (!img.entry_id && (!img.itinerary_id || img.itinerary_id === currentItineraryId)) return true;
      return false;
    }
  );

  useEffect(() => {
    // Auto-focus textarea on mount to trigger keyboard
    const t = setTimeout(() => {
      try { textareaRef.current?.focus(); } catch (e) {}
    }, 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const canonical = resolveMapRouteModeMeta(initialMapLinkMode)?.key || (initialMapLinkMode || '');
    setMapLinkMode(canonical);
  }, [initialMapLinkMode]);

  useEffect(() => {
    setMapLinkDuration(initialMapLinkDuration || '');
  }, [initialMapLinkDuration]);

  const handleSave = async () => {
    if (!cardId || !currentUserId || !onGalleryUpdate) {
      // 没有图片上传功能，直接保存文本
      try {
        setLocalSaving(true);
        const normalizedLink = normalizeExternalLink(mapLink);
        const normalizedDuration = normalizedLink ? (typeof mapLinkDuration === 'string' ? mapLinkDuration.trim() : '') : '';
        const normalizedMode = normalizedLink ? (resolveMapRouteModeMeta(mapLinkMode)?.key || (mapLinkMode || '').trim()) : '';
        await onSave({
          text,
          mapLink: normalizedLink,
          mapLinkMode: normalizedMode || undefined,
          mapLinkDuration: normalizedDuration || undefined,
        });
      } catch (e) {
        // caller handles error display
      } finally {
        setLocalSaving(false);
      }
      return;
    }

    // 有图片需要上传
    try {
      setLocalSaving(true);
      setUploading(false);

      console.log('💾 开始保存，待上传图片:', pendingImages.length);

      // 1. 先保存文本内容（获取 entryId）
      const normalizedLink = normalizeExternalLink(mapLink);
      const normalizedDuration = normalizedLink ? (typeof mapLinkDuration === 'string' ? mapLinkDuration.trim() : '') : '';
      const normalizedMode = normalizedLink ? (resolveMapRouteModeMeta(mapLinkMode)?.key || (mapLinkMode || '').trim()) : '';
      
      const saveResult = await onSave({
        text,
        mapLink: normalizedLink,
        mapLinkMode: normalizedMode || undefined,
        mapLinkDuration: normalizedDuration || undefined,
      });

      // 获取 entryId
      const currentEntryId = (saveResult && typeof saveResult === 'object' && saveResult.entryId) || entryId;
      
      console.log('✅ 文本保存完成, entryId:', currentEntryId);

      // 2. 如果有图片，上传完成后再更新
      if (pendingImages.length > 0 && currentEntryId && onGalleryUpdate) {
        setUploading(true);
        const uploadedImages = [];
        
        for (const { file } of pendingImages) {
          console.log('📤 上传图片:', file.name);

          const form = new FormData();
          form.append('file', file);
          form.append('userId', currentUserId);
          form.append('cardId', cardId);
          form.append('folder', 'gallery');

          const resp = await fetch(CARD_IMAGE_UPLOAD_ENDPOINT, {
            method: 'POST',
            body: form,
            mode: 'cors',
          });

          if (!resp.ok) {
            const text = await resp.text();
            throw new Error(`上传失败: ${resp.status} ${text}`);
          }

          const result = await resp.json();
          if (!result.success || !result.data) {
            throw new Error('上传服务返回无效响应');
          }

          uploadedImages.push({
            url: result.data.url,
            key: result.data.key,
            uploaded_by: currentUserId,
            uploaded_at: result.data.uploadTime || new Date().toISOString(),
            itinerary_id: currentItineraryId || undefined,
            entry_id: currentEntryId || undefined, // 添加 entry_id
          });

          console.log('✅ 图片上传成功:', result.data.url);
        }

        // 3. 通知父组件更新（传递完整 gallery，方便父级同步卡片）
        const updatedGallery = [...gallery, ...uploadedImages];
        console.log('💾 通知更新，完整 gallery 数量:', updatedGallery.length);
        await onGalleryUpdate(updatedGallery, currentEntryId);
        console.log('✅ onGalleryUpdate 执行完成');
        
        // 清空待上传列表
        setPendingImages([]);
      }

      console.log('✅ 全部保存完成');
      
      // 关闭编辑器
      if (onCancel) {
        onCancel();
      }

    } catch (e) {
      console.error('❌ 保存失败:', e);
      alert(`保存失败: ${e instanceof Error ? e.message : '未知错误'}`);
      throw e;
    } finally {
      setLocalSaving(false);
      setUploading(false);
    }
  };

  // 图片选择处理（本地预览，不立即上传）
  const handleSelectFile = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const currentCount = itineraryImages.length + pendingImages.length;
    if (currentCount >= MAX_IMAGE_COUNT) {
      alert(`最多只能上传${MAX_IMAGE_COUNT}张图片`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    let availableSlots = MAX_IMAGE_COUNT - currentCount;
    const acceptedFiles: File[] = [];
    const oversizedFiles: string[] = [];

    for (const file of files) {
      if (availableSlots <= 0) break;
      if (file.size > MAX_FILE_SIZE) {
        oversizedFiles.push(file.name);
        continue;
      }
      acceptedFiles.push(file);
      availableSlots -= 1;
    }

    const skippedDueToLimit = files.length - oversizedFiles.length - acceptedFiles.length > 0;

    if (acceptedFiles.length === 0) {
      const messages: string[] = [];
      if (oversizedFiles.length) messages.push(`以下档案超过 8MB：${oversizedFiles.join(', ')}`);
      if (skippedDueToLimit) messages.push('已达到 9 张上限，没有更多图片可以加入。');
      alert(messages.join('\n') || '没有可加入的图片');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const newPreviews = acceptedFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setPendingImages((prev) => [...prev, ...newPreviews]);

    const warningMessages: string[] = [];
    if (oversizedFiles.length) warningMessages.push(`以下档案超过 8MB 并已略过：${oversizedFiles.join(', ')}`);
    if (skippedDueToLimit) warningMessages.push('已达到 9 张上限，部分图片未加入。');
    if (warningMessages.length) {
      alert(warningMessages.join('\n'));
    }

    console.log('📷 添加图片到预览:', acceptedFiles.map((file) => file.name).join(', '));

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // 删除待上传的图片（本地预览）
  const handleRemovePendingImage = (index: number) => {
    setPendingImages(prev => {
      const newList = [...prev];
      URL.revokeObjectURL(newList[index].preview);
      newList.splice(index, 1);
      return newList;
    });
  };

  // 删除已上传的图片
  const handleDeleteUploadedImage = async (imageKey: string) => {
    if (!currentUserId || !cardId || !onGalleryUpdate) return;

    if (!confirm('确定要删除这张图片吗？')) return;

    try {
      console.log('🗑️ 删除图片:', imageKey);

      const resp = await fetch(CARD_IMAGE_UPLOAD_ENDPOINT, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: imageKey, userId: currentUserId, cardId }),
        mode: 'cors',
      });

      if (!resp.ok) {
        const text = await resp.text();
        console.error('❌ 删除失败:', resp.status, text);
        throw new Error(`删除失败: ${resp.status}`);
      }

      const result = await resp.json();
      console.log('✅ 删除成功:', result);

      const updatedGallery = gallery.filter(img => img.key !== imageKey);
      await onGalleryUpdate(updatedGallery, entryId);

      console.log('✅ 数据库更新成功');

    } catch (err) {
      console.error('❌ 删除图片错误:', err);
      alert(`删除失败: ${err instanceof Error ? err.message : '未知错误'}`);
    }
  };

  // 清理预览 URL
  useEffect(() => {
    return () => {
      pendingImages.forEach(({ preview }) => URL.revokeObjectURL(preview));
    };
  }, [pendingImages]);

  const canSave = Boolean(String(text).trim() || normalizeExternalLink(mapLink));
  const modeMeta = resolveMapRouteModeMeta(mapLinkMode);
  const resolvedModeKey = modeMeta?.key || mapLinkMode;

  // 使用相对定位的编辑器，让它出现在点击位置，使用原生键盘
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 12, 
      padding: 16, 
      background: '#fff', 
      borderRadius: 16, 
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      border: '2px solid #FB7A4A',
      margin: '0 12px 12px 12px',
      maxWidth: '600px',
      width: 'calc(100% - 24px)'
    }}>


      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="写下你的小记（心情 / 提醒 / 小事项）..."
        style={{ 
          minHeight: 120, 
          maxHeight: 300, 
          resize: 'vertical', 
          padding: 12, 
          borderRadius: 10, 
          border: '1px solid #e5e7eb', 
          fontSize: 15, 
          outline: 'none',
          fontFamily: 'inherit',
          lineHeight: '1.5'
        }}
        inputMode="text"
        autoCorrect="on"
        spellCheck={true}
        autoComplete="on"
        aria-label="小记输入"
      />

      {/* 图片区域 - 显示在文字输入框下方 */}
      {cardId && currentUserId && onGalleryUpdate && (
        <div style={{ marginTop: -4 }}>
          {/* 没有图片时显示相机占位符 */}
          {itineraryImages.length === 0 && pendingImages.length === 0 && (
            <div
              onClick={handleSelectFile}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '32px 16px',
                cursor: 'pointer',
                borderRadius: 8,
                background: '#F9FAFB',
                border: '1px dashed #D1D5DB',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#F3F4F6';
                e.currentTarget.style.borderColor = '#9CA3AF';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#F9FAFB';
                e.currentTarget.style.borderColor = '#D1D5DB';
              }}
            >
              <WebIcon name="photo-camera" size={40} color="#9CA3AF" family="MaterialIcons" />
              <span style={{ marginTop: 8, fontSize: 13, color: '#6B7280' }}>
                点击添加图片
              </span>
            </div>
          )}

          {/* 有图片时显示图片网格 */}
          {(itineraryImages.length > 0 || pendingImages.length > 0) && (
            <div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 8,
                marginBottom: 12,
              }}>
                {/* 已上传的图片 */}
                {itineraryImages.map((img, idx) => (
                  <div
                    key={img.key || idx}
                    style={{
                      position: 'relative',
                      paddingBottom: '100%',
                      borderRadius: 8,
                      overflow: 'hidden',
                      background: '#F3F4F6',
                    }}
                  >
                    <img
                      src={img.url}
                      alt={`图片 ${idx + 1}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    {/* 删除按钮 */}
                    {img.uploaded_by === currentUserId && (
                      <button
                        type="button"
                        onClick={() => handleDeleteUploadedImage(img.key)}
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          background: 'rgba(0, 0, 0, 0.6)',
                          border: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          padding: 0,
                        }}
                      >
                        <WebIcon name="close" size={16} color="#fff" family="MaterialIcons" />
                      </button>
                    )}
                  </div>
                ))}

                {/* 待上传的图片 */}
                {pendingImages.map(({ preview }, idx) => (
                  <div
                    key={`pending-${idx}`}
                    style={{
                      position: 'relative',
                      paddingBottom: '100%',
                      borderRadius: 8,
                      overflow: 'hidden',
                      background: '#F3F4F6',
                      border: '2px dashed #FF6B00',
                    }}
                  >
                    <img
                      src={preview}
                      alt={`待上传 ${idx + 1}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    {/* 待上传标识 */}
                    <div
                      style={{
                        position: 'absolute',
                        top: 4,
                        left: 4,
                        padding: '2px 6px',
                        background: 'rgba(255, 107, 0, 0.9)',
                        color: '#fff',
                        fontSize: 10,
                        borderRadius: 4,
                        fontWeight: '500',
                      }}
                    >
                      待上传
                    </div>
                    {/* 删除按钮 */}
                    <button
                      type="button"
                      onClick={() => handleRemovePendingImage(idx)}
                      style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: 'rgba(0, 0, 0, 0.6)',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    >
                      <WebIcon name="close" size={16} color="#fff" family="MaterialIcons" />
                    </button>
                  </div>
                ))}

                {/* 添加更多图片按钮（最多9张）*/}
                {itineraryImages.length + pendingImages.length < MAX_IMAGE_COUNT && (
                  <div
                    onClick={handleSelectFile}
                    style={{
                      position: 'relative',
                      paddingBottom: '100%',
                      borderRadius: 8,
                      overflow: 'hidden',
                      background: '#F9FAFB',
                      border: '1px dashed #D1D5DB',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#F3F4F6';
                      e.currentTarget.style.borderColor = '#9CA3AF';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#F9FAFB';
                      e.currentTarget.style.borderColor = '#D1D5DB';
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <WebIcon name="add" size={32} color="#9CA3AF" family="MaterialIcons" />
                    </div>
                  </div>
                )}
              </div>

              {/* 图片计数 */}
              <div style={{ textAlign: 'center', fontSize: 12, color: '#9CA3AF' }}>
                {itineraryImages.length + pendingImages.length}/{MAX_IMAGE_COUNT}
              </div>
            </div>
          )}

          {/* 隐藏的文件选择器 - 支持拍照（mobile）和相册选择 */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
      )}
      <input
        value={mapLink}
        onChange={(e) => setMapLink(e.target.value)}
        placeholder="貼上 Google 地圖路線連結（選填）"
        style={{
          height: 44,
          padding: '0 12px',
          borderRadius: 10,
          border: '1px solid #e5e7eb',
          fontSize: 14,
          outline: 'none',
          fontFamily: 'inherit',
        }}
        inputMode="url"
        autoCorrect="off"
        spellCheck={false}
        autoComplete="off"
        aria-label="Google 地圖路線連結"
      />
      {mapLink ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ marginBottom: 8, fontSize: 13, color: '#6B7280' }}>選擇交通方式</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {ROUTE_MODE_OPTIONS.map((option) => {
                const selected = option.key === resolvedModeKey;
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setMapLinkMode((prev) => (prev === option.key ? '' : option.key))}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 14px',
                      borderRadius: 12,
                      border: selected ? '2px solid #2563EB' : '1px solid #E5E7EB',
                      background: selected ? 'rgba(37, 99, 235, 0.12)' : '#fff',
                      color: selected ? '#1D4ED8' : '#4B5563',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: selected ? 600 : 500,
                    }}
                  >
                    <WebIcon name={option.icon} size={16} color={selected ? '#2563EB' : '#6B7280'} family="MaterialIcons" />
                    <span>{option.shortLabel || option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div style={{ marginBottom: 8, fontSize: 13, color: '#6B7280' }}>預估行程時間</div>
            <input
              value={mapLinkDuration}
              onChange={(e) => setMapLinkDuration(e.target.value)}
              placeholder="例如 38 分鐘或 2.7 公里"
              style={{
                height: 44,
                padding: '0 12px',
                borderRadius: 10,
                border: '1px solid #e5e7eb',
                fontSize: 14,
                outline: 'none',
                fontFamily: 'inherit',
              }}
              inputMode="text"
              autoCorrect="off"
              spellCheck={false}
              autoComplete="off"
              aria-label="預估行程時間"
            />
          </div>
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button 
          onClick={onCancel} 
          type="button" 
          style={{ 
            padding: '12px 20px', 
            background: '#f3f4f6', 
            border: 'none', 
            borderRadius: 10, 
            cursor: 'pointer',
            fontSize: 15,
            fontWeight: '500'
          }}
        >
          取消
        </button>
        <button 
          onClick={handleSave} 
          type="button" 
          disabled={localSaving || saving || !canSave} 
          style={{ 
            padding: '12px 20px', 
            background: (localSaving || saving || !canSave) ? '#d1d5db' : '#FB7A4A', 
            color: '#fff', 
            border: 'none', 
            borderRadius: 10, 
            cursor: (localSaving || saving || !canSave) ? 'not-allowed' : 'pointer',
            fontSize: 15,
            fontWeight: '500'
          }}
        >
          {uploading ? '上传中...' : (localSaving || saving) ? '保存中...' : '保存'}
        </button>
      </div>
    </div>
  );
};

export default NoteEditor;
